import React, { Component } from 'react';
import { DashboardTable } from '@/features/dashboard-table/DashboardTable';
import {
  QUERY_RESULTS,
  SINGLE_VALUE,
} from '@/components/ChartHolder.constants';
import {
  BOOL,
  DATE,
  DATETIME,
  FLOAT,
  INT,
  INTERVAL,
  STRING,
  UUID,
} from '../util/ColumnType';
import ChartJS, { ChartDataLabels } from './ChartJSConfiguration';
import ChartSingleValue from './ChartSingleValue';
import PivotTable from './PivotTable';
import {
  CONFIG_IS_DONUT,
  CONFIG_ROTATE_DATA_LABELS,
  CONFIG_SHOW_DATA_LABELS,
  CONFIG_SHOW_LEGEND,
  SECTION_COLUMN_NAMES,
} from './charting/chartingConfiguration.constants';
import {
  buildDatasets,
  secondColIsDimension,
  transformDatatableIfNecessary,
} from '../util/helpers/charting-helpers';
import { cellFormatter } from '../util/helpers/chart-formatting-helpers';
import { DAY, MONTH, WEEK, YEAR } from '../util/enums/EnumDateTimeAggregation';
import { CHART_CANVAS } from '../constants/test-ids';
import {
  BAR,
  BARLINE,
  LINE,
  PIE,
  PIVOT,
  STACKED_CHART,
  TABLE,
} from './charting/chartType.constants';

class ChartHolder extends Component {
  constructor(props) {
    super(props);
    this.state = {
      // only show animation when switching between presentation types
      // NOTE: we don't show animations in dashboard as it sometimes causes charts not to render
      showAnimation: false,
      currentPresentation: props.presentation,
    };
    this.getChartOption = this.getChartOption.bind(this);
    this.getColumnLabel = this.getColumnLabel.bind(this);
  }

  static getDerivedStateFromProps(props, state) {
    if (state.currentPresentation !== props.presentation) {
      return {
        currentPresentation: props.presentation,
        showAnimation: true,
      };
    }
    if (state.showAnimation) {
      // props changed before animation was finished, so don't restart animation
      return {
        showAnimation: false,
      };
    }
    return null; // don't update state
  }

  static canHave2ndYAxis(headings) {
    if (!headings) {
      return false;
    }
    const sCID = secondColIsDimension(headings, this.getChartOption);
    const noOfDimensions = sCID ? 2 : 1;

    // metrics is the difference between the headings length and the number of dimensions
    const noOfMetrics = headings.length - noOfDimensions;
    return noOfDimensions === 1 && noOfMetrics === 2;
  }

  static canHandleHeadings(headings, presentation = null) {
    if (!headings) {
      return false;
    }

    if (
      headings.length > 3 &&
      [LINE, BAR, null].includes(presentation) &&
      [STRING, DATE, DATETIME, BOOL, UUID, INT].includes(
        headings[0].columnType().type(),
      ) &&
      headings
        .slice(1)
        .every((h) => [INT, FLOAT].includes(h.columnType().type()))
    ) {
      // allow for line/bar charts with 1 dimension and multiple metrics
      return true;
    }

    const result =
      ([1].includes(headings.length) && presentation === SINGLE_VALUE) ||
      ([2, 3].includes(headings.length) &&
        [STRING, DATE, DATETIME, BOOL, INT, UUID, FLOAT].includes(
          headings[0].columnType().type(),
        ) &&
        [STRING, DATE, DATETIME, BOOL, INT, UUID, FLOAT].includes(
          headings[headings.length - 2].columnType().type(),
        ) &&
        [INT, FLOAT].includes(
          headings[headings.length - 1].columnType().type(),
        ));

    if (presentation !== PIVOT && !result) {
      return false;
    }

    if (presentation) {
      switch (presentation) {
        case BAR:
          return [2, 3].includes(headings.length);
        case LINE:
          return [2, 3].includes(headings.length);
        case PIE: // or STACKED
          return [2, 3].includes(headings.length);
        case BARLINE:
          return [3].includes(headings.length);
        case PIVOT:
          return [3, 4, 5].includes(headings.length);
        case SINGLE_VALUE:
          return (
            ([1].includes(headings.length) &&
              [INT, FLOAT, STRING, INTERVAL].includes(
                headings[0].columnType().type(),
              )) ||
            ChartHolder.canBeSingleValueWithTrend(headings)
          );
      }
    }
    return true;
  }

  static dimensions(headings) {
    return headings.length > 2 &&
      ![INT, FLOAT].includes(headings[1].columnType().type())
      ? 2 // two columns represent x axis
      : 1; // only one column on x axis
  }

  static canBeSingleValueWithTrend(headings) {
    if (!headings) {
      return false;
    }
    return (
      headings.length === 2 &&
      [DATE, DATETIME].includes(headings[0].columnType().type()) &&
      [INT, FLOAT].includes(headings[1].columnType().type())
    );
  }

  // returns true if the datatable has loaded and it's a single column and a single row. Headings are passed in in ChartToggle as a fallback so that the single value chart option appears when adding a widget to the dashboard
  static isSingleCell(dt, headings) {
    if (!dt && !headings) {
      return false;
    }

    const check = (_headings) => {
      return (
        _headings.length === 1 &&
        [INT, FLOAT, STRING, INTERVAL].includes(
          _headings[0].columnType().type(),
        )
      );
    };

    if (dt) {
      return check(dt.headings()) && dt.rows().length === 1;
    }

    return check(headings);
  }

  static canComparePeriods(dataTable, presentation, showCompareToggle) {
    if (!dataTable) {
      return false;
    }
    const allowedAggregations = [DAY, MONTH];
    // if showCompareToggle is true, then include WEEK, and YEAR (this is used to show the toggle in the chart settings, even though it's disabled as week and year are not supported currently)
    if (showCompareToggle) {
      allowedAggregations.push(WEEK);
      allowedAggregations.push(YEAR);
    }

    return (
      [DATE, DATETIME].includes(dataTable.headings()[0].columnType().type()) &&
      allowedAggregations.includes(dataTable.headings()[0].aggregation()) &&
      dataTable.headings().length === 2 &&
      [LINE, BAR].includes(presentation)
    );
  }

  isPie() {
    return (
      this._dimensions === 1 &&
      this._presentation === PIE &&
      this._dataTable.headings().length === 2
    );
  }

  isStacked = () => this._dimensions === 2 && this._presentation === PIE;

  isBar() {
    return this._presentation === BAR;
  }

  componentDidMount() {
    this.updateChart();
  }

  componentDidUpdate() {
    if (this._existing) {
      this._existing.destroy();
    }
    this.updateChart();
  }

  componentWillUnmount() {
    if (this._existing) {
      this._existing.destroy();
    }
  }

  updateChart() {
    if (!this._canvas) {
      return; // not rendering a canvas, so nothing to update
    }

    const {
      xAxis,
      yAxes: yAxesArr,
      datasets,
    } = buildDatasets(
      this._dataTable,
      this._presentation,
      this._dimensions,
      this.getChartOption.bind(this),
    );

    const yAxes = yAxesArr.reduce(
      (result, y) => ({ ...result, [y.id ?? 'y']: y }),
      {},
    );

    const showLabels = this.getChartOption(CONFIG_SHOW_DATA_LABELS);
    const rotateLabels = this.getChartOption(CONFIG_ROTATE_DATA_LABELS);

    const formatTooltipValue = (_datasets, datasetIndex) => {
      const colPos =
        this._dimensions === 2
          ? 2
          : this._presentation === BARLINE
          ? this._dataTable.headings().length - datasetIndex - 1 // datasets have been reversed for barline
          : this._dataTable.headings().length - _datasets.length + datasetIndex;
      return cellFormatter(this._dataTable, this.getChartOption, colPos);
    };

    const getLayoutTopMargin = () => {
      if (showLabels && rotateLabels) {
        return 60;
      }

      if (showLabels) {
        return 33;
      }

      return 20;
    };

    const getLayoutRightMargin = () => {
      if (showLabels && !rotateLabels) {
        return 20;
      }

      return 0;
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      scales: this.isPie()
        ? undefined
        : {
            ...yAxes,
            x: {
              stacked: this.isStacked(),
            },
          },
      layout: {
        padding: {
          left: 0,
          right: getLayoutRightMargin(),
          top: getLayoutTopMargin(),
          bottom: 0,
        },
      },
      plugins: {
        // data labels chart-level options (https://chartjs-plugin-datalabels.netlify.app/guide/getting-started.html#configuration)
        datalabels: {
          formatter(value, context) {
            const { datasets: _datasets } = context.chart.config.data;
            const { datasetIndex } = context;
            const formatted = formatTooltipValue(
              _datasets,
              datasetIndex,
            )(value);
            // extract number part of formatted value
            const formattedNumber = Number(
              `${formatted}`.replace(/[^0-9\.]+/g, ''),
            );
            if (formattedNumber === 0) {
              // hide data labels with value 0
              return '';
            }
            return formatted;
          },
          labels: {
            title: {
              font: {
                weight: 'bold',
                size: this.props.isDashboard ? 11 : 12,
              },
            },
          },
          rotation: this.getChartOption(CONFIG_ROTATE_DATA_LABELS) ? -90 : 0,
          anchor: this.isStacked() ? 'center' : 'end',
          align: this.isPie() ? 'start' : this.isStacked() ? 'center' : 'end',
          display: 'auto', // hide overlapping labels
        },
        legend: {
          position: 'bottom',
          strokeStyle: '#fff',
          display: this.getChartOption(CONFIG_SHOW_LEGEND),
          labels: {
            // https://www.chartjs.org/docs/latest/configuration/legend.html#legend-label-configuration
            boxWidth: 12,
            font: {
              family: "'DM Sans', sans-serif",
              size: 11,
            },
          },
        },
        tooltip: {
          callbacks: {
            title(tooltipItems) {
              return tooltipItems[0].label;
            },
            label(tooltipItem) {
              const { label } = tooltipItem.dataset;
              const value = tooltipItem.formattedValue;

              return `${label}: ${value}`;
            },
          },
        },
      },
      animation: {
        duration: this.state.showAnimation ? 1000 : 0,
        onComplete: () => {
          // only run animation once (once complete, turn it off)
          if (this.state.showAnimation) {
            this.setState({
              showAnimation: false,
            });
          }
        },
      },
    };

    const formatXAxis = cellFormatter(this._dataTable, this.getChartOption, 0);

    let chartDataSets = datasets;

    // chartjs builds the chart based on the order of the datasets. Need to reverse these for bar-line charts so that the line appears on top.
    if (this._presentation === BARLINE && datasets.length === 2) {
      const first = datasets[0];
      const second = datasets[1];
      chartDataSets = [second, first];
    }

    const config = {
      type:
        this.isStacked() || this._presentation === BARLINE
          ? BAR
          : this.isPie()
          ? this.getChartOption(CONFIG_IS_DONUT)
            ? 'doughnut'
            : 'pie'
          : this._presentation, // line or bar or pie
      data: {
        labels: xAxis.map((x) => formatXAxis(x)),
        datasets: chartDataSets,
      },
      plugins: !showLabels ? undefined : [ChartDataLabels],
      options,
    };
    this._existing = new ChartJS(this._canvas, config);
  }

  getChartOption(configItem) {
    const section = configItem[0];
    const item = configItem[1];
    if (!this.props.chartingOptions) {
      return null;
    }
    const sectionObj = this.props.chartingOptions[section] || {};
    const result = sectionObj[item];
    // undefined is used to control default value
    if (result === undefined && configItem.length > 2) {
      return configItem[2];
    }
    return result;
  }

  getColumnLabel(heading) {
    const subsection = [SECTION_COLUMN_NAMES, heading.columnName().toString()];
    const value = this.getChartOption(subsection)
      ? this.getChartOption(subsection)
      : heading.columnName().name().replace(/\_/g, ' ');
    return value;
  }

  render() {
    const { dataTable, isDashboard, chartContainerHeight } = this.props;
    let { presentation } = this.props;

    if (!ChartHolder.canHandleHeadings(dataTable.headings(), presentation)) {
      presentation = TABLE;
    }

    if (presentation === STACKED_CHART) {
      presentation = PIE;
    }
    if (isDashboard && ChartHolder.isSingleCell(dataTable)) {
      presentation = SINGLE_VALUE;
    }

    const style = !isDashboard ? { margin: '20px 50px' } : {};

    if (chartContainerHeight) {
      style.overflow = 'auto';
    }

    // height for shareHTML page
    if (chartContainerHeight) {
      style.height = `${chartContainerHeight}px`;
    }

    if (presentation === QUERY_RESULTS) presentation = TABLE;

    if (presentation === PIVOT) {
      return (
        <PivotTable
          getColumnLabel={this.getColumnLabel}
          isDashboard={isDashboard}
          dataTable={dataTable}
          getChartOption={this.getChartOption}
          chartContainerHeight={chartContainerHeight}
          identifier={Math.floor(Math.random() * 100)}
        />
      );
    }

    if (presentation === SINGLE_VALUE) {
      return (
        <ChartSingleValue
          isDashboard={isDashboard}
          dataTable={dataTable}
          getChartOption={this.getChartOption}
          isSingleCell={ChartHolder.isSingleCell(dataTable)}
          isSingleValueWithTrend={ChartHolder.canBeSingleValueWithTrend(
            dataTable.headings(),
          )}
        />
      );
    }

    if (presentation === TABLE) {
      return (
        <div className="dashboards h-100" style={style}>
          <DashboardTable
            isDashboard={isDashboard}
            dataTable={dataTable}
            getChartOption={this.getChartOption}
            getColumnLabel={this.getColumnLabel}
          />
        </div>
      );
    }

    const heightStyle = chartContainerHeight
      ? { height: `${chartContainerHeight}px` }
      : { height: '100%' };

    const chartClass = 'chart-holder';

    this._presentation = presentation;

    // transform table as per toggled flipped dims / comparing periods settings etc.
    this._dataTable = transformDatatableIfNecessary(
      dataTable,
      this.getChartOption,
      presentation,
    );

    this._dimensions = secondColIsDimension(
      this._dataTable.headings(),
      this.getChartOption,
      presentation,
    )
      ? 2
      : 1;

    return (
      <div className={chartClass} style={heightStyle}>
        <canvas
          ref={(canvas) => (this._canvas = canvas)}
          data-testid={CHART_CANVAS}
        />
      </div>
    );
  }
}

export default ChartHolder;
